











import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { throttle, clamp } from 'lodash';
import { Direction, Box } from '@/core/components/tiles/types';
import { getBoxStyle, getBoxWidth, getBoxHeight } from './utils';

const RESIZE_THROTTLE_MS = 1000 / 30; // 30 fps

@Component
export default class Splitter extends Vue {
    @Prop(String) public direction: Direction | undefined;
    @Prop() public boundingBox: Box | undefined;
    @Prop(Number) public ratio: number | undefined;
    @Prop({type: Number, default: 0.25 }) public minRatio: number | undefined;
    @Prop(Boolean) public inPx: boolean | undefined;

    private onMouseMoveThrottled = throttle(this.onMouseMove.bind(this), RESIZE_THROTTLE_MS);
    private clientHeight: number | null = null;
    private clientWidth: number | null = null;

    public beforeDestroy() {
        document.removeEventListener('mouseup', this.onMouseUp, true);
        document.removeEventListener('touchend', this.onMouseUp, true);
        document.removeEventListener('mousemove', this.onMouseMove, true);
        document.removeEventListener('touchmove', this.onMouseMove, true);
    }

    public calculateStyle() {
        let box: Box | undefined;

        if (this.direction === Direction.Horizontal) {
            box = {
                ...this.boundingBox!,
                left: this.boundingBox!.left + getBoxWidth(this.boundingBox!) * this.ratio!,
            };
        } else if (this.direction === Direction.Vertical) {
            box = {
                ...this.boundingBox!,
                top: this.boundingBox!.top + getBoxHeight(this.boundingBox!) * this.ratio!,
            };
        }

        return getBoxStyle(box!);
    }

    public calculateStyleInPx() {
        let left: number = 0;
        let top: number = 0;
        const right: number = 0;
        const bottom: number = 0;
        if (this.direction === Direction.Horizontal) {
            left = (this.clientWidth !== null ? this.clientWidth : document.documentElement.clientWidth) *
                   (this.boundingBox!.left + getBoxWidth(this.boundingBox!) * this.ratio!)
                   / 100;
        } else if (this.direction === Direction.Vertical) {
            top = (this.clientHeight !== null ? this.clientHeight : document.documentElement.clientHeight) *
                  (this.boundingBox!.top + getBoxWidth(this.boundingBox!) * this.ratio!)
                  / 100;
        }

        return `left: ${left}px;
                top: ${top}px;
                right: ${right}px;
                bottom: ${bottom}px;`;
    }

    public getSplitterClass() {
        return {
            's-tile-splitter-h': this.direction === Direction.Horizontal,
            's-tile-splitter-v': this.direction === Direction.Vertical,
        };
    }

    public onMouseDown(event: MouseEvent | TouchEvent) {
        if (event instanceof MouseEvent && event.button !== 0) {
            return;
        }

        if (event instanceof MouseEvent) {
            event.preventDefault();
        }
        document.addEventListener('mouseup', this.onMouseUp, true);
        document.addEventListener('touchend', this.onMouseUp, true);
        document.addEventListener('mousemove', this.onMouseMove, true);
        document.addEventListener('touchmove', this.onMouseMove, true);
    }

    private onMouseMove(event: MouseEvent | TouchEvent) {
        if (event instanceof MouseEvent) {
            event.preventDefault();
        }
        event.stopPropagation();

        const ratio = this.calculateRatio(event);
        if (ratio !== this.ratio) {
            this.$emit('change', ratio);
        }
    }

    private onMouseUp(event: MouseEvent | TouchEvent) {
        document.removeEventListener('mousemove', this.onMouseMove, true);
        document.removeEventListener('touchmove', this.onMouseMove, true);
        document.removeEventListener('mouseup', this.onMouseUp, true);
        document.removeEventListener('touchend', this.onMouseUp, true);

        const ratio = this.calculateRatio(event);
        if (ratio !== this.ratio) {
            this.$emit('release', ratio);
        }
    }

    private calculateRatio(event: MouseEvent | TouchEvent): number {
        const rootElement = this.$refs.root! as Element;
        const parentRect = rootElement.parentElement!.getBoundingClientRect();
        if (this.clientHeight === null || this.clientWidth === null) {
            this.clientHeight = parentRect.height;
            this.clientWidth = parentRect.width;
        }
        const box = this.boundingBox!;
        const rect = this.getRectFromBox(box, parentRect);
        let ratio: number | undefined;
        if (this.direction === Direction.Vertical) {
            const clientY = event instanceof MouseEvent ? event.clientY : event.changedTouches[0].clientY;
            const parentY = clientY - parentRect.top;
            ratio = (parentY - rect.top) / rect.height;
        } else if (this.direction === Direction.Horizontal) {
            const clientX = event instanceof MouseEvent ? event.clientX : event.changedTouches[0].clientX;
            const parentX = clientX - parentRect.left;
            ratio = (parentX - rect.left) / rect.width;
        }

        return clamp(ratio!, this.minRatio!, 1 - this.minRatio!);
    }

    private getRectFromBox(relativeBox: Box, parent: ClientRect | DOMRect): ClientRect {
        const left = this.clientWidth! * relativeBox.left / 100;
        const right = this.clientWidth! * relativeBox.right / 100;
        const top = this.clientHeight! * relativeBox.top / 100;
        const bottom = this.clientHeight! * relativeBox.bottom / 100;
        return {
            left,
            right,
            top,
            bottom,
            width: this.clientWidth! - left - right,
            height: this.clientHeight! - top - bottom,
        };
    }
}
